<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { formatDate } from "@core/helpers"
import { useComparatorStore } from "@modules/courtage/stores/comparator.store"
import moment from "moment"
import { storeToRefs } from "pinia"
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            best: "{startDate} - {endDate} (période idéale détectée par homeys)",
            custom: "Période personnalisée",
            date_from: "Date de début de la période de consommation",
            date_from_hint:
                "La date de début de la période de consommation est conditionnée à 1 an avant la date de fin de la période de consommation.",
            date_to: "Date de fin de la période de consommation",
            error_fetching:
                "Une erreur est survenue lors de la récupération des périodes de consommation disponibles. Veuillez sélectionner une période personnalisée.",
            no_best_periode:
                "Aucune période idéale de 1 an sans trou de donnée a été détectée. Veuillez sélectionner une période personnalisée.",
        },
    },
})

// ================================
// REACTIVE STATE
// ================================
/**
 * Store du comparateur pour gérer les périodes
 */
const comparatorStore = useComparatorStore()

/**
 * Références réactives du store pour les périodes
 */
const {
    getSourceById,
    periodSelected,
    bestPeriodAvailable,
    periodsAvailable,
    modePeriod,
    isFetchingPeriods,
    hasErrorPeriods,
} = storeToRefs(comparatorStore)

/**
 * Date de fin de la période personnalisée
 */
const dateTo = ref<Date | null>(null)

/**
 * Date de début de la période personnalisée (calculée automatiquement)
 */
const dateFrom = ref<Date | null>(null)

// ================================
// COMPUTED PROPERTIES
// ================================
/**
 * Calcule la date maximale autorisée basée sur les périodes disponibles
 * @returns Date maximale ou date actuelle si aucune période disponible
 */
const getMaxDate = computed((): Date => {
    if (periodsAvailable.value.length === 0) {
        return new Date()
    }

    // Récupère la période la plus récente et utilise sa date de fin comme maximum
    const maxDate = periodsAvailable.value.reduce((max, current) => {
        return new Date(current.date_to) > new Date(max.date_to) ? current : max
    }, periodsAvailable.value[0])
    return new Date(maxDate.date_to)
})

const getDateToSoutirage = computed(() => {
    return getSourceById.value?.informations?.pdl?.data?.soutirage?.date_to
})

/**
 * Génère le libellé de la période idéale avec les dates formatées
 * @returns Libellé formaté de la période idéale ou chaîne vide
 */
const getBestPeriodLabel = computed((): string => {
    if (bestPeriodAvailable.value) {
        return t("best", {
            startDate: formatDate(
                bestPeriodAvailable.value.date_from,
                "DD/MM/YYYY"
            ),
            endDate: formatDate(
                bestPeriodAvailable.value.date_to,
                "DD/MM/YYYY"
            ),
        })
    }
    return ""
})

// ================================
// WATCHERS
// ================================
/**
 * Surveille les changements de dates pour mettre à jour la période sélectionnée
 * en mode personnalisé
 */
watch(
    [dateFrom, dateTo],
    () => {
        if (modePeriod.value === "custom") {
            updatePeriodSelected()
        }
    },
    {
        immediate: true,
    }
)

watch(hasErrorPeriods, (newVal) => {
    if (newVal) {
        modePeriod.value = "custom"
        // Met la date de fin au maximum disponible
        if (getDateToSoutirage.value)
            dateTo.value = new Date(getDateToSoutirage.value)
        else {
            dateTo.value = moment().toDate()
        }
        onUpdateDateTo(dateTo.value) // Met à jour la date de début en conséquence
    } else {
        // Réinitialise les dates si plus d'erreur
        dateTo.value = null
        dateFrom.value = null
        periodSelected.value = null
        modePeriod.value = "best"
    }
})

// ================================
// METHODS
// ================================
/**
 * Met à jour la date de début automatiquement quand la date de fin change
 * La date de début est toujours 1 an avant la date de fin
 * @param value - Nouvelle date de fin sélectionnée
 */
function onUpdateDateTo(value: Date): void {
    if (moment(value).isValid()) {
        // Date de début = Date de fin - 1 an
        dateFrom.value = moment(value)
            .subtract(1, "year")
            .add(1, "day")
            .toDate()
    } else {
        dateFrom.value = null
    }
}

/**
 * Met à jour la période sélectionnée dans le store avec les dates personnalisées
 * Réinitialise la période si les dates ne sont pas valides
 */
function updatePeriodSelected(): void {
    if (!moment(dateFrom.value).isValid() || !moment(dateTo.value).isValid()) {
        // Réinitialise periodSelected si les dates ne sont pas valides
        periodSelected.value = null
        return
    }

    // Met à jour periodSelected avec les dates personnalisées formatées
    periodSelected.value = {
        date_from: moment(dateFrom.value).format("YYYY-MM-DD"),
        date_to: moment(dateTo.value).format("YYYY-MM-DD"),
    }
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <div class="comparator-meter-info-period">
        <BaseMessage severity="error" class="mb-4" v-if="hasErrorPeriods">
            {{ t(!bestPeriodAvailable ? "no_best_periode" : "error_fetching") }}
        </BaseMessage>
        <!-- ================================ -->
        <!-- OPTIONS DE PÉRIODE -->
        <!-- ================================ -->
        <div v-if="!isFetchingPeriods" class="flex flex-col gap-4">
            <!-- Option période idéale détectée -->
            <BaseInputWrapper
                v-if="bestPeriodAvailable"
                orientation="horizontal"
                unfloat
                input-id="period-best"
                :label="getBestPeriodLabel"
            >
                <BaseRadioButton
                    input-id="period-best"
                    name="period-mode"
                    value="best"
                    v-model="modePeriod"
                />
            </BaseInputWrapper>

            <!-- Option période personnalisée -->
            <BaseInputWrapper
                orientation="horizontal"
                unfloat
                input-id="period-custom"
                :label="t('custom')"
            >
                <BaseRadioButton
                    input-id="period-custom"
                    name="period-mode"
                    value="custom"
                    v-model="modePeriod"
                />
            </BaseInputWrapper>
        </div>

        <BaseSkeleton v-else width="100%" height="5rem" />

        <!-- ================================ -->
        <!-- FORMULAIRE DE DATES PERSONNALISÉES -->
        <!-- ================================ -->
        <div
            v-if="modePeriod === 'custom'"
            class="flex flex-wrap gap-4 items-start"
        >
            <!-- Sélecteur de date de début -->
            <BaseInputWrapper
                unfloat
                :label="t('date_from')"
                :hint="t('date_from_hint')"
                class="flex-[1_0_12rem]"
                input-id="date-from"
            >
                <BaseDatePicker
                    v-model="dateFrom"
                    date-format="dd/mm/yy"
                    show-icon
                    placeholder="--/--/----"
                    :max-date="getMaxDate"
                    icon-display="input"
                    disabled
                    input-id="date-from"
                />
            </BaseInputWrapper>

            <!-- Sélecteur de date de fin -->
            <BaseInputWrapper
                unfloat
                :label="t('date_to')"
                class="flex-[1_0_12rem]"
                input-id="date-to"
            >
                <BaseDatePicker
                    v-model="dateTo"
                    date-format="dd/mm/yy"
                    show-icon
                    placeholder="--/--/----"
                    :max-date="new Date()"
                    icon-display="input"
                    @update:model-value="onUpdateDateTo"
                    input-id="date-to"
                />
            </BaseInputWrapper>
        </div>
    </div>
</template>
